import { zodResolver } from "@hookform/resolvers/zod";
import { Image as ImageIcon, Upload } from "lucide-react";
import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { FileUpload } from "~/components/ui/file-upload";
import { Input } from "~/components/ui/input";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import {
  CategoryFormSchema,
  type CategoryFormData,
  CATEGORY_NAME_MAX_LENGTH,
  CATEGORY_SHORT_DESCRIPTION_MAX_LENGTH,
} from "~/lib/schemas/category-of-life";
import { MAX_FILE_SIZE_BYTES } from "~/lib/schemas/resource";

interface CategoryFormProps {
  initialData?: CategoryFormData;
  existingImageUrl?: string | null;
  onSubmit: (data: CategoryFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const CategoryForm = ({
  initialData,
  existingImageUrl,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: CategoryFormProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(
    initialData?.file || null,
  );
  const [showExistingImage, setShowExistingImage] =
    useState(!!existingImageUrl);
  const isSubmittingRef = useRef(false);

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(CategoryFormSchema),
    defaultValues: {
      name: initialData?.name || "",
      shortDescription: initialData?.shortDescription || "",
      file: initialData?.file || null,
    },
  });

  const handleFileSelect = (files: File[]) => {
    const file = files[0];
    if (file) {
      setSelectedFile(file);
      form.setValue("file", file, { shouldValidate: true });
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setShowExistingImage(false);
    form.setValue("file", null, { shouldValidate: true });
    form.setValue("removeExistingImage", true);
  };

  const handleSubmit = (data: CategoryFormData) => {
    if (isSubmitting || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    onSubmit(data);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)}>
      <Card className="shadow-none hover:shadow-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <h1 className="text-xl font-medium text-gray-900">
            {initialData ? "Edit Life Category" : "Add Life Category"}
          </h1>
          <div className="flex items-center gap-3">
            <Button type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="rounded-xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-6">
              <div className="space-y-2">
                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <Input
                      placeholder="Category Title"
                      maxLength={CATEGORY_NAME_MAX_LENGTH}
                      {...field}
                    />
                  )}
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-red-600">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <RichTextEditor
                content={initialData?.shortDescription || ""}
                onChange={(html) => form.setValue("shortDescription", html)}
                placeholder="Start writing your vision..."
                className="h-[400px]"
                maxLength={CATEGORY_SHORT_DESCRIPTION_MAX_LENGTH}
              />
              {form.formState.errors.shortDescription && (
                <p className="text-sm text-red-600">
                  {form.formState.errors.shortDescription.message}
                </p>
              )}
            </div>

            <div className="h-fit">
              {!selectedFile && !showExistingImage ? (
                <div className="h-[200px]">
                  <FileUpload
                    onChange={handleFileSelect}
                    accept="image/svg+xml,image/png,image/jpeg,image/gif"
                    maxSize={MAX_FILE_SIZE_BYTES}
                    recommendedDimensions={{ width: 1200, height: 675 }}
                    label="Click to upload or drag and drop"
                    fullHeight
                  />
                </div>
              ) : (
                /* File/Image Preview */
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-5">
                  <div className="flex items-start gap-4">
                    {/* Preview Image */}
                    <div className="flex h-[134px] w-[238px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gray-100">
                      {selectedFile ? (
                        selectedFile.type.startsWith("image/") ? (
                          <img
                            src={URL.createObjectURL(selectedFile)}
                            alt="Preview"
                            className="h-full w-full rounded-xl object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center rounded-xl bg-gray-200">
                            <ImageIcon className="h-8 w-8 text-gray-400" />
                          </div>
                        )
                      ) : showExistingImage && existingImageUrl ? (
                        <img
                          src={existingImageUrl}
                          alt="Current image"
                          className="h-full w-full rounded-xl object-cover"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-xl bg-gray-200">
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex flex-1 flex-col justify-center space-y-3">
                      <div className="space-y-1">
                        <p className="text-base font-normal text-gray-900">
                          {selectedFile ? selectedFile.name : "Current image"}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          {selectedFile ? (
                            <>
                              <span>
                                {Math.round(selectedFile.size / 1024)}kb
                              </span>
                              <span>•</span>
                              <span>Uploaded</span>
                            </>
                          ) : (
                            <span>Previously uploaded</span>
                          )}
                        </div>
                      </div>

                      {/* File Actions */}
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <Button
                            type="button"
                            className="rounded-xl"
                            onClick={() =>
                              document
                                .getElementById("hidden-file-upload")
                                ?.click()
                            }
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            Upload new
                          </Button>
                          <input
                            id="hidden-file-upload"
                            type="file"
                            accept="image/svg+xml,image/png,image/jpeg,image/gif"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                handleFileSelect(Array.from(e.target.files));
                              }
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          className="rounded-xl"
                          onClick={handleRemoveFile}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};
